import { PDFDocument } from 'pdf-lib';

export type PdfParagraph = { text: string; heading?: boolean };
export const wrapPdfText = (text: string, measure: (value: string) => number, width: number): string[] => {
  const lines: string[] = [];
  for (const paragraph of text.split('\n')) {
    let line = '';
    for (const character of Array.from(paragraph)) {
      if (line && measure(line + character) > width) {
        const space = line.lastIndexOf(' ');
        lines.push(space > 0 ? line.slice(0, space) : line);
        line = space > 0 ? line.slice(space + 1) : '';
      }
      line += character;
    }
    lines.push(line);
  }
  return lines;
};

// Browser fonts preserve the same Unicode text shown in the preview. Each A4 page
// is embedded at 150 dpi, so PDF generation needs no PHP renderer or remote font.
export const downloadCandidatePdf = async (paragraphs: PdfParagraph[], filename: string, seal?: HTMLImageElement) => {
  const pdf = await PDFDocument.create();
  const canvas = document.createElement('canvas');
  canvas.width = 1240;
  canvas.height = 1754;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('PDF rendering is unavailable in this browser.');
  const margin = 80;
  let y = margin;
  const startPage = () => {
    context.fillStyle = '#fff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = '#111';
    context.textBaseline = 'top';
    y = margin;
  };
  const savePage = async () => {
    const image = await pdf.embedPng(canvas.toDataURL('image/png'));
    pdf.addPage([595.28, 841.89]).drawImage(image, { x: 0, y: 0, width: 595.28, height: 841.89 });
  };
  startPage();
  if (seal) {
    context.drawImage(seal, margin, y, 150, 150);
    y += 175;
  }
  for (const paragraph of paragraphs) {
    context.font = paragraph.heading ? 'bold 26px Arial, sans-serif' : '24px Arial, sans-serif';
    const lines = wrapPdfText(paragraph.text, (line) => context.measureText(line).width, canvas.width - margin * 2);
    for (const line of lines) {
      if (y + 36 > canvas.height - margin) {
        await savePage();
        startPage();
      }
      context.fillText(line, margin, y);
      y += 36;
    }
    y += 20;
  }
  await savePage();
  const bytes = await pdf.save();
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  try {
    anchor.click();
  } finally {
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
};
